import CircularProgress from '@mui/material/CircularProgress'
import type { DigitalTwinTranslations } from 'api/digitalTwin/digitalTwin.api'
import type {
  AppliedSettingSeries,
  AttributeChip,
  OptJobRun,
} from 'helpers/appliedSettingValues.helper/appliedSettingValues.types'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, LoadingPlaceholderContainer } from 'ui/atoms'
import { AppliedSettingValuesPanel } from './AppliedSettingValuesPanel'
import { useAppliedSettingValueDatasets } from './useAppliedSettingValueDatasets'

type AppliedSettingValuesContentProps = {
  selectedSeries: AppliedSettingSeries[]
  runs: OptJobRun[]
  overrideAlias: UiConfigAliases
  period: { startTime: ISODateTime; endTime: ISODateTime }
  priorityLevels: number[]
  translations: DigitalTwinTranslations
  showGraph: boolean
  searchTerm: string
  onlyUnitsWithOutcome: boolean
  showPropertyChips: boolean
  attributeChips: AttributeChip[]
  selectedAttributes: string[]
  onSelectedAttributesChange: (attributes: string[]) => void
  hideEmptyColumns: boolean
}

export function AppliedSettingValuesContent({
  selectedSeries,
  runs,
  overrideAlias,
  period,
  priorityLevels,
  translations,
  showGraph,
  searchTerm,
  onlyUnitsWithOutcome,
  showPropertyChips,
  attributeChips,
  selectedAttributes,
  onSelectedAttributesChange,
  hideEmptyColumns,
}: AppliedSettingValuesContentProps): ReactNode {
  const { t } = useTranslation()
  const { status, isFetching, datasets, retry } = useAppliedSettingValueDatasets({
    selection: selectedSeries,
    runs,
    overrideAlias,
    period,
    priorityLevels,
  })

  if (status === 'pending') {
    return <LoadingPlaceholderContainer height={100} />
  }
  if (status === 'error') {
    return (
      <div className="flex flex-col items-start gap-2">
        <p>{t('Applied setting values could not be loaded')}</p>
        <Button primary onClick={retry}>
          {t('Try again')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="h-1">
        {isFetching && <CircularProgress size={20} aria-label={t('Updating applied setting values')} />}
      </div>
      <AppliedSettingValuesPanel
        key={selectedSeries.map((series) => series.returnId).join(',')}
        series={selectedSeries}
        datasets={datasets}
        translations={translations}
        showGraph={showGraph}
        searchTerm={searchTerm}
        onlyUnitsWithOutcome={onlyUnitsWithOutcome}
        showPropertyChips={showPropertyChips}
        attributeChips={attributeChips}
        selectedAttributes={selectedAttributes}
        onSelectedAttributesChange={onSelectedAttributesChange}
        hideEmptyColumns={hideEmptyColumns}
      />
    </div>
  )
}
